// index.tsx
import * as React from 'react';
import { PageProps } from '../../types/navigationProperties';
import { PaginationRequest } from '../../types/pagination';
import { Presentation, getAllPresentations } from '../../services/PresentationsAboutServiceProvider';
import { useTranslation } from 'react-i18next';
import { Grid, Box, TablePagination } from '@mui/material';
import Footer from '../../components/Footer';
import PageHeader from '../../components/PageHeader';
import CardVideoGallery from '../../components/CardVideoGallery';
import { displayErrorToast } from '../../utils/toasts';
import LoaderSpinner from '../../components/LoaderSpinner';
import { LightShotSingle } from '../../components/LightShotSingle';
import { GalleryImage } from '../../types/galleryImage';
import videoPresentationPage from '../../assets/img/video_presentation_page.jpg';
import { SetButtonCallbackFunction } from '../../types/callback';
import { SEO } from '../../components/SEO';
import { MetaTag } from '../../services/SEOServiceProvider';

export default function PresentationList({ props, setPageName, metaTags }: { props: PageProps, setPageName: SetButtonCallbackFunction, metaTags: MetaTag[] }) {
    const { t } = useTranslation();
    const headingProps = {title: t("video_presentations.title"), description: undefined};
    const language = props.language;
    const [loading, setLoading] = React.useState(true);
    const [page, setPage] = React.useState(0);
    const [data, setData] = React.useState<Presentation[]>();
    const [total, setTotal] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(8);
    const pageSizeOptions: number[] = [4, 8, 12, 16];
    const [pagination, setPagination] = React.useState<PaginationRequest>({page: page, page_size: pageSize});
    const [open, setOpen] = React.useState(false);
    const [selectedImage, setSelectedImage] = React.useState<GalleryImage>();
    const [metaTag, setMetaTag] = React.useState<MetaTag>();

    const handlePaginationChange = () => {
        setPagination({page: page, page_size: pageSize});
    }

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
        handlePaginationChange();
    };

    const handleChangePageSize = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 8));
        setPage(0);
        handlePaginationChange();
    };

    const handleOpenLightShot = (image: string, name: string) => {
        let item: GalleryImage = { src: image, name: name };
        setSelectedImage(item);
        setOpen(true);
    }

    React.useEffect(() => {
        console.log(pagination);
        setPageName('video-presentation');
        let metaTagData = metaTags !== undefined && metaTags.filter((item: MetaTag) => item.page === 'video-presentation' );
        if(metaTagData && metaTagData.length > 0) {
            let presentationPageMetaTag: MetaTag = {...metaTagData[0]} as MetaTag;
            presentationPageMetaTag.url = window.location.href;
            presentationPageMetaTag.image = videoPresentationPage;
            setMetaTag(presentationPageMetaTag);
        }

        (async () => {
            try {
                setLoading(true);
                const presentationsData = await getAllPresentations(pagination);
                console.log(presentationsData);
                setData(presentationsData.results);
                setTotal(presentationsData.count);
            } catch (error) {
                displayErrorToast(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [language, pagination, setPageName, metaTags]);

    return loading ? (
        <div>
            <SEO metaTag={metaTag} />
            <LoaderSpinner loading={loading} />
        </div>
    ) : (
        <Box>
            <SEO metaTag={metaTag} />
            <LightShotSingle image={selectedImage} opened={open} setOpenLightShot={setOpen} />
            <Box>
                <PageHeader props={headingProps} />
            </Box>
            <Box sx={{ py: '40px', px: '20px', paddingTop: { xs: '0px', sm: '0px', md: '40px' }, bgcolor: 'primary.light' }}
                 style={{ width: '100%', height: '100%' }}>
                <TablePagination component="div"
                              count={total}
                              page={page}
                              onPageChange={handleChangePage}
                              rowsPerPage={pageSize}
                              labelRowsPerPage={t('table.pagination.label_rows_per_page')}
                              labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${t('table.pagination.of')} ${
                                        count !== -1 ? count : `${t('table.pagination.more_than')} ${to}`
                                    }`
                              }
                              getItemAriaLabel={(type) => t(`table.pagination.aria_label_${type}`)}
                              rowsPerPageOptions={pageSizeOptions}
                              onRowsPerPageChange={handleChangePageSize} />
                <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{ display: 'flex' }}>
                        <img loading="lazy" src={videoPresentationPage}
                            style={{
                                position: 'relative',
                                margin: 'auto',
                                maxWidth: '500px',
                                width: '100%',
                            }}
                            alt="video presentation page" onClick={() => handleOpenLightShot(videoPresentationPage, "video presentation page")} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }} sx={{
                                    display: 'flex',
                                    position: 'relative',
                                    marginTop: 'auto',
                                    marginBottom: 'auto',
                                    height: '100%',
                                    paddingTop: { xs: '40px', sm: '40px', md: '0px' }
                                }}>
                        <CardVideoGallery props={data} pageProps={props} />
                    </Grid>
                </Grid>
                <TablePagination component="div"
                              count={total}
                              page={page}
                              onPageChange={handleChangePage}
                              rowsPerPage={pageSize}
                              labelRowsPerPage={t('table.pagination.label_rows_per_page')}
                              labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${t('table.pagination.of')} ${
                                        count !== -1 ? count : `${t('table.pagination.more_than')} ${to}`
                                    }`
                              }
                              getItemAriaLabel={(type) => t(`table.pagination.aria_label_${type}`)}
                              rowsPerPageOptions={pageSizeOptions}
                              onRowsPerPageChange={handleChangePageSize} />
            </Box>
            <Footer {...props} />
        </Box>
    )
}

